import { useCallback, useState } from 'react';

import {
  PathsDesignsDesignIdPresentationsPresentationIdInvitesGetResponses200ContentApplicationJsonResultsReceiverRole as AccessRole,
  PathsCollaborationInvitesGetParametersQueryResourceType as CollaborationResourceType,
  PathsSubscriptionAdminChangePostRequestBodyApplicationJsonPlan as WorkspacePlan,
  useCollaborationInvitesCreateMutation,
  useWorkspaceQuery,
} from '@repo/api-client';
import { useProofOfWork } from '@repo/editor/src/hooks/useProofOfWork';
import {
  useSubscriptionStore,
  subscriptionStoreSelector,
} from '@repo/subscription/client';
import { validators } from '@repo/ui/components/Inputs';
import { fireToast } from '@repo/ui/components/Toast';
import { Button } from '@repo/ui-kit/components/core/Button';
import { Dialog } from '@repo/ui-kit/components/core/Dialog';
import { TagInput } from '@repo/ui-kit/components/core/Input';
import { Select } from '@repo/ui-kit/components/core/Select';
import { P4 } from '@repo/ui-kit/components/core/Typography';

import { useWorkspaceStore } from 'stores/workspaceStore';
import { FlexColumn } from 'styles/layouts';

import {
  Container,
  Title,
  SubmitArea,
  DialogTitle,
  SelectItemWrapper,
} from './styles';
import { WorkspaceInviteOptions } from './useWorkspaceInviteDialog';

const ROLE_OPTIONS = [
  {
    value: 'editor',
    label: 'Editor',
    description: 'Can edit team projects and invite.',
  },
  {
    value: 'viewer',
    label: 'Viewer',
    description: 'Can view team projects.',
  },
];

const isValidEmail = (email: string) => {
  return Boolean(!validators.email(email));
};

export interface WorkspaceInviteDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  options: WorkspaceInviteOptions;
}

export const WorkspaceInviteDialog: React.FC<WorkspaceInviteDialogProps> = ({
  isOpen,
  setIsOpen,
  options,
}) => {
  const [emails, setEmails] = useState<string[]>([]);

  const [disabled, setDisabled] = useState<boolean>(true);

  const { isCreation = false, infoText } = options;

  const [role, setRole] = useState<AccessRole>(AccessRole.editor);
  const { mutateAsync: inviteToWorkspace, isPending } =
    useCollaborationInvitesCreateMutation();

  const currentWorkspaceId = useWorkspaceStore(
    (state) => state.activeWorkspaceId
  );

  const { data: currentWorkspace } = useWorkspaceQuery({
    workspaceId: currentWorkspaceId,
  });

  // Get showUpgradeModal from the subscription store
  const showUpgradeModal = useSubscriptionStore(
    subscriptionStoreSelector.showUpgradeModal
  );

  const handleDialogOpenChange = useCallback(
    (isOpen: boolean) => {
      setEmails([]);
      setIsOpen(isOpen);
    },
    [setIsOpen]
  );

  const handleChange = useCallback((newEmails: string[]) => {
    setEmails(newEmails);
    setDisabled(newEmails.length === 0);
  }, []);

  const handleChanging = useCallback(
    (newEmail: string) => {
      if (newEmail === '') {
        setDisabled(emails.length === 0);
        return;
      }

      setDisabled(!isValidEmail(newEmail.trim()));
    },
    [emails]
  );

  const { fetchChallenge } = useProofOfWork();

  const handleSubmit = useCallback(async () => {
    if (!currentWorkspaceId) {
      fireToast({
        label: 'Failed to invite',
        error: true,
      });
      return;
    }
    try {
      const challenge = await fetchChallenge();
      await inviteToWorkspace({
        params: {
          header: {
            'x-challenge-nonce': challenge.nonce ?? '',
            'x-challenge-solution': challenge.solution?.toString() ?? '',
          },
        },
        body: {
          resourceId: currentWorkspaceId,
          resourceType: CollaborationResourceType.workspace,
          users: emails.map((email) => ({ email, role })),
        },
      });

      fireToast({
        label: emails.length === 1 ? 'Member invited' : 'Members invited',
        error: false,
      });
      handleDialogOpenChange(false);

      setDisabled(true);

      // Show upgrade modal if the workspace was just created
      if (isCreation) {
        showUpgradeModal('noIncentiveUpgrade', {
          trackingLabel: 'freeLimitReached',
        });
      }
    } catch (e) {
      fireToast({
        label: 'Failed to invite',
        error: true,
      });
    }
  }, [
    currentWorkspaceId,
    fetchChallenge,
    inviteToWorkspace,
    emails,
    handleDialogOpenChange,
    isCreation,
    role,
    showUpgradeModal,
  ]);

  return (
    <Dialog.Root
      modal={true}
      open={isOpen}
      onOpenChange={handleDialogOpenChange}
    >
      <Dialog.Content minHeight="385px" title="Invite members" width="400px">
        <Container>
          <DialogTitle padding="16px 0 16px 24px">
            <Title bold>Invite members</Title>
          </DialogTitle>
          <FlexColumn gap="16px" padding="16px 24px 24px 24px">
            <TagInput
              isLarge
              isValid={isValidEmail}
              label="Email"
              placeholder="Type email..."
              value={emails}
              onChanged={handleChange}
              onChanging={handleChanging}
            />
            <FlexColumn gap="4px">
              <Select.Root
                label="Invite as"
                size="m"
                value={role}
                onValueChange={(value) => setRole(value as AccessRole)}
              >
                <Select.Trigger placeholder={role} />
                <Select.Content position="popper">
                  {ROLE_OPTIONS.map((option) => (
                    <SelectItemWrapper key={option.value}>
                      <Select.Item key={option.value} value={option.value}>
                        <FlexColumn justifyContent="space-between" width="100%">
                          <Select.ItemText label={option.label} />
                          <P4>{option.description}</P4>
                        </FlexColumn>
                      </Select.Item>
                    </SelectItemWrapper>
                  ))}
                </Select.Content>
              </Select.Root>

              <P4>
                {infoText}
                {role === 'editor' &&
                  currentWorkspace?.plan !== WorkspacePlan.FREE &&
                  'Additional seats will be added to your invoice, at your current price.'}
              </P4>
            </FlexColumn>
            <SubmitArea>
              <Button
                disabled={disabled}
                isLoading={isPending}
                name="inviteMembers"
                onClick={handleSubmit}
              >
                <Button.Label>{isPending ? '' : 'Invite'}</Button.Label>
              </Button>
            </SubmitArea>
          </FlexColumn>
        </Container>
      </Dialog.Content>
    </Dialog.Root>
  );
};
